import { Box } from '@mui/material'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import List from 'components/core/list/index'
import { RelativeTimestamp } from 'components/core/time/display'
import { useErrorReporting } from 'components/no-ui/error-reporting/datadog'
import { AppDisplayName } from 'components/pages/app/app-display-name/index'
import { AppUserDisplayName } from 'components/pages/app/users/app-user-display-name'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { QUERIES } from 'components/pages/explore/query/Query'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { AppUserValue } from 'lib/actions/apps/app-users'
import {
    HighRiskRolesAndGrantsEntitlementsRequest,
    HighRiskRolesAndGrantsStruct,
} from 'pbts/c1/api/app/v1/app_resource'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { FC, useMemo, useState, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { highRiskRolesandGrantsEntitlements } from 'redux_toolkit/app-resources'
import { ExportData } from './ExportData'
import { StructuredSearchStyles } from './styles'

const cols: Array<C1TableReduxColumn<HighRiskRolesAndGrantsStruct>> = [
    {
        field: 'user',
        label: <FormattedMessage id="high_risk_roles_user" defaultMessage="User" />,
        Component: ({ row }) => (row.user ? <UserDisplayName user={row.user} /> : '–'),
    },
    {
        field: 'account',
        label: <FormattedMessage id="high_risk_roles_account" defaultMessage="Account" />,
        Component: ({ row }) => (row.appUser ? <AppUserDisplayName appUser={row.appUser} /> : '–'),
    },
    {
        field: 'app',
        label: <FormattedMessage id="high_risk_roles_app" defaultMessage="App" />,
        Component: ({ row }) => (row.app ? <AppDisplayName app={row.app} avatarSize="xs" /> : '–'),
    },
    {
        field: 'appResource',
        label: <FormattedMessage id="high_risk_roles_role" defaultMessage="Role" />,
        Component: ({ row }): React.ReactElement | string => {
            const appEntitlementValue = {
                ...row.entitlement,
                app: row.app,
                appResourceType: row.resourceType,
                appResource: row.resource,
            } as AppEntitlementValue
            return (row.resource && row.app && row.resourceType && row.entitlement
                ? <C1EntitlementDisplayName entitlement={appEntitlementValue} textTruncationWidth="300px" />
                : <C1EmptyIndicator />)
        },
    },
    {
        field: 'expires',
        label: <FormattedMessage id="high_risk_roles_expires" defaultMessage="Expires on" />,
        Component: ({ row }): React.ReactElement => {
            return (row.deprovisionAt ? <Box>
                <RelativeTimestamp
                    date={row.deprovisionAt}
                    variant="date_only"
                    typographyVariant="body1"
                />
            </Box> : <Box>
                <FormattedMessage id="components_user_list_no_expiration" defaultMessage="No expiration" />
            </Box>)
        },
    },
]

export const HighRiskRoleGrantsReportTemporary: FC = () => {
    const [page, setPage] = useState<number>(0)
    const { data,
        isError,
        isLoading,
        error,
        getPage,
        canLoadMore } = highRiskRolesandGrantsEntitlements.useHighRiskRolesAndGrantsEntitlements(
        HighRiskRolesAndGrantsEntitlementsRequest.create({
            onGoing: false,
        }),
        { autoReload: true },
    )

    const onPageChange = useCallback(
        async (n: number) => {
            setPage(n)
            await getPage(n)
        },
        [getPage],
    )

    useErrorReporting(isError, error, QUERIES.HighRiskRoleGrantsTemporary)

    const table = useMemo(
        () => (
            <C1TableRedux<HighRiskRolesAndGrantsStruct>
                data={data}
                cols={cols}
                getId={(row, i): string => ((row.appUser && i !== undefined) ? row.appUser.id + i.toString() : crypto.randomUUID())}
                isError={isError}
                isLoading={isLoading}
                canLoadMore={canLoadMore}
                error={error}
                page={page}
                onPageChange={onPageChange}
            />
        ),
        [canLoadMore, data, error, isError, isLoading, onPageChange, page],
    )

    const exportDataActionRequest = useMemo(
        () => ExportDataRequest.create({
            queryId: QueryId.HIGH_RISK_ROLE_GRANTS_TEMPORARY,
            request: {
                oneofKind: 'highRiskRolesAndGrantsRequest',
                highRiskRolesAndGrantsRequest: HighRiskRolesAndGrantsEntitlementsRequest.create({ onGoing: false }),
            },
        }),
        [],
    )

    return <>
        <Box sx={StructuredSearchStyles.exportDataBox}>
            <ExportData exportDataRequest={exportDataActionRequest} />
        </Box>
        <List<AppUserValue> table={table} />
    </>
}

export const HighRiskRoleGrantsReportPermanent: FC = () => {
    const [page, setPage] = useState<number>(0)
    const { data,
        isError,
        isLoading,
        error,
        getPage,
        canLoadMore } = highRiskRolesandGrantsEntitlements.useHighRiskRolesAndGrantsEntitlements(
        HighRiskRolesAndGrantsEntitlementsRequest.create({
            onGoing: true,
        }),
        { autoReload: true },
    )

    const onPageChange = useCallback(
        async (n: number) => {
            setPage(n)
            await getPage(n)
        },
        [getPage],
    )
    useErrorReporting(isError, error, QUERIES.HighRiskRoleGrantsPermanent)

    const table = useMemo(
        () => (
            <C1TableRedux<HighRiskRolesAndGrantsStruct>
                data={data}
                cols={cols.slice(0, cols.length - 1)}
                getId={(row, i): string => ((row.appUser && i !== undefined) ? row.appUser.id + i.toString() : crypto.randomUUID())}
                isError={isError}
                isLoading={isLoading}
                canLoadMore={canLoadMore}
                error={error}
                page={page}
                onPageChange={onPageChange}
            />
        ),
        [canLoadMore, data, error, isError, isLoading, onPageChange, page],
    )

    const exportDataActionRequest = useMemo(() => {
        return ExportDataRequest.create({
            queryId: QueryId.HIGH_RISK_ROLE_GRANTS_PERMANENT,
            request: {
                oneofKind: 'highRiskRolesAndGrantsEntitlementsRequest',
                highRiskRolesAndGrantsEntitlementsRequest: HighRiskRolesAndGrantsEntitlementsRequest.create({ onGoing: true }),
            },
        })
    }, [])

    return <>
        <Box sx={StructuredSearchStyles.exportDataBox}>
            <ExportData exportDataRequest={exportDataActionRequest} />
        </Box>
        <List<AppUserValue> table={table} />
    </>
}
